
import { useState, useEffect } from 'react';
import { useCentralizedAuth } from '@/context/CentralizedAuthProvider';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { signupWithEmailPassword } from '@/services/authService';

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  age?: string;
  phone?: string;
  educationStatus?: string;
}

export const useSignupForm = () => {
  const { isAuthenticated, isLoading: authLoading } = useCentralizedAuth();
  const navigate = useNavigate();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [age, setAge] = useState<number | undefined>(undefined);
  const [phone, setPhone] = useState('');
  const [educationStatus, setEducationStatus] = useState<string>('completed12th');
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessingSignup, setIsProcessingSignup] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [formError, setFormError] = useState('');
  const [isChecking, setIsChecking] = useState(true);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    timeoutId = setTimeout(() => {
      console.log('Auth check timeout - continuing with signup form');
      setIsChecking(false);
    }, 1000);
    
    if (isAuthenticated) {
      console.log('User is already authenticated, redirecting');
      navigate('/');
    } else {
      console.log('User is not authenticated, showing signup form');
      setIsChecking(false);
    }
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated && !isProcessingSignup) {
      console.log('User is authenticated, redirecting to home');
      navigate('/');
    }
  }, [isAuthenticated, navigate, isProcessingSignup]);

  useEffect(() => {
    if (!authLoading && isLoading) {
      setIsLoading(false);
    }
  }, [authLoading, isLoading]);

  const validateForm = () => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!name.trim()) {
      newErrors.name = 'Name is required';
      isValid = false;
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
      isValid = false;
    }

    if (!password) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
      isValid = false;
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    if (!phone) {
      newErrors.phone = 'Phone number is required';
      isValid = false;
    } else if (!/^\d{10}$/.test(phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone number must be 10 digits';
      isValid = false;
    }

    if (age === undefined) {
      newErrors.age = 'Age is required';
      isValid = false;
    } else if (age < 10 || age > 100) {
      newErrors.age = 'Age must be between 10 and 100';
      isValid = false;
    }
    
    if (!educationStatus) {
      newErrors.educationStatus = 'Education status is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setFormError('Please fix the form errors');
      return;
    }
    
    setFormError('');
    setIsLoading(true);
    setIsProcessingSignup(true);
    
    try {
      await signupWithEmailPassword(
        name,
        email,
        password,
        phone,
        age,
        educationStatus
      );
      
      // Show success message instead of toast
      setShowSuccessMessage(true);
      
    } catch (error: any) {
      console.error('Signup error:', error);
      
      if (error.message && error.message.includes('already registered')) {
        setFormError('This email is already registered. Please log in instead.');
      } else {
        setFormError(error.message || 'Signup failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
      setIsProcessingSignup(false);
    }
  };

  return {
    name,
    setName,
    email,
    setEmail,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    age,
    setAge,
    phone,
    setPhone,
    educationStatus,
    setEducationStatus,
    isLoading,
    authLoading,
    errors,
    formError,
    setFormError,
    isChecking,
    isAuthenticated,
    showSuccessMessage,
    handleSignup,
  };
};
